/**
 * TheProcessPage — public marketing page that mirrors Part 4 of the full pitch
 * PDF. Shows the 9-step ServiceSell PRO flow side-by-side with the broken
 * "today" flow. Designed to be shared as a URL — dealers tap a link, get the
 * pitch story without needing a PDF in their inbox.
 *
 * Route: /the-process  (public, no auth required)
 */
import { Link } from "react-router-dom";
import {
  Camera, Wrench, Tag, Banknote, MessageSquare, Sparkles,
  CheckCircle2, Mail, BarChart3, Clock, ArrowRight,
} from "lucide-react";
import { BRAND_PROFILES } from "@/lib/brandProfile";
import { usePublicTheme } from "@/lib/usePublicTheme";

const OLD_FLOW = [
  { time: "½ min",    text: "Technician submits VHC + video. Job moves to parts." },
  { time: "5–10 min", text: "Parts juggles 6 techs, phone calls, walk-ins. VHC waits its turn." },
  { time: "3–5 min",  text: "Parts tags lines, gets pricing right. One wrong tag, wrong job sold." },
  { time: "5–10 min", text: "Advisor has 7-8 customers, phones ringing, walk-ins at the counter." },
  { time: "3–5 min",  text: "Advisor prints VHC. Walks to workshop control for labour times." },
  { time: "3–5 min",  text: "Advisor prices job. Calls customer. Voicemail. Leaves a message." },
];

const NEW_FLOW = [
  { icon: Camera,        title: "Tech taps Generate Quote",
    body: "AI vision reads the VHC photos + video. Drafts plain-English explanation in your workshop voice. Tech reviews, sends.",
    duration: "30 seconds" },
  { icon: Tag,           title: "Parts pricing auto-attached",
    body: "Live integration to your parts system. Correct parts, correct prices, against the right VHC line items. No hand-tagging.",
    duration: "Instant" },
  { icon: Wrench,        title: "Labour times auto-pulled",
    body: "Standard labour times from your menus. Workshop control isn't pulled away from the diary.",
    duration: "Instant" },
  { icon: Banknote,      title: '"Fitted Price" shown to customer',
    body: "Parts + labour + VAT in ONE number per recommendation. No mental maths, no surprise, faster approvals.",
    duration: "—" },
  { icon: MessageSquare, title: "SMS + email with tap-to-approve link",
    body: "No app. No login. Customer taps the link on their phone — sees photos, video, plain-English explanation, taps Approve.",
    duration: "Median 4 mins" },
  { icon: Sparkles,      title: "AI Objection Handler on declines",
    body: "If the customer hesitates, Claude writes a warm, tailored re-approach addressing the likely objection — price, urgency, trust, or timing.",
    duration: "Real-time" },
  { icon: CheckCircle2,  title: "Workshop control gets the green light",
    body: "Live ping the moment the customer approves. The car never leaves the ramp. No diary re-juggling.",
    duration: "Live" },
  { icon: Mail,          title: "Service plan auto-pitch",
    body: "Every customer without a plan gets a 120-word personalised pitch email. Service-plan poll auto-credits commission on the sale.",
    duration: "Hands-off" },
  { icon: BarChart3,     title: "Dealer ROI Dashboard live",
    body: "Inside the app: monthly approved-extras revenue, fees paid, ROI multiple. Renewal conversation in month 12 writes itself.",
    duration: "Live" },
];

export default function TheProcessPage() {
  usePublicTheme();
  // Public marketing page — always default brand. White-label demo is gated
  // to the post-login app so prospects see canonical SSP first.
  const brand = BRAND_PROFILES.default;
  const trade = brand.key === "default" ? "\u2122" : "";
  return (
    <div className="min-h-screen bg-background text-foreground" data-testid="the-process-page">

      {/* -------- HERO -------- */}
      <section className="relative overflow-hidden border-b border-white/10 bg-black">
        <div className="absolute inset-0 bg-gradient-to-br from-primary/[0.08] via-transparent to-destructive/[0.05] pointer-events-none" />
        <div className="relative max-w-6xl mx-auto px-6 py-20 md:py-28">
          <Link to="/" className="text-[10px] font-bold uppercase tracking-[0.3em] text-primary mb-6 inline-flex items-center gap-2" data-testid="tp-back-home">
            {brand.productName}{trade} &middot; The Process
          </Link>
          <h1 className="font-display font-bold text-4xl sm:text-5xl lg:text-6xl leading-[1.05] text-white mt-4 mb-6">
            From 20–40 minutes <br />
            <span className="text-primary">to 4 minutes.</span>
          </h1>
          <p className="text-base sm:text-lg text-white/70 max-w-2xl leading-relaxed">
            The way most service drives put a quote in front of a customer hasn&apos;t really changed
            in two decades. {brand.productName}{trade} rebuilds the path from the technician&apos;s phone to the
            customer&apos;s screen — without changing how your team works.
          </p>
        </div>
      </section>

      {/* -------- OLD WAY -------- */}
      <section className="border-b border-white/10 bg-black">
        <div className="max-w-6xl mx-auto px-6 py-16">
          <div className="text-[10px] font-bold uppercase tracking-[0.25em] text-destructive">
            A day on the service drive · today
          </div>
          <h2 className="font-display font-bold text-3xl md:text-4xl text-white mt-3 mb-8">
            How a quote actually gets to a customer.
          </h2>
          <p className="text-white/65 max-w-3xl leading-relaxed mb-10">
            A car is on the ramp, mid-service. The technician finds an issue. Here&apos;s where the
            clock starts.
          </p>
          <ol className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="tp-old-flow">
            {OLD_FLOW.map((step, idx) => (
              <li key={idx} className="rounded-lg border border-destructive/25 bg-destructive/[0.04] p-5 flex items-start gap-4">
                <div className="font-mono-plex font-bold text-destructive text-sm whitespace-nowrap min-w-[64px]">{step.time}</div>
                <div className="text-white/80 text-sm leading-relaxed">{step.text}</div>
              </li>
            ))}
          </ol>
          <div className="mt-10 rounded-xl border border-destructive/40 bg-destructive/10 p-6 text-center">
            <div className="text-destructive font-mono-plex text-xs font-bold uppercase tracking-[0.3em]">
              By now
            </div>
            <div className="font-display font-bold text-3xl md:text-4xl text-white mt-2 mb-2">
              20–40 minutes elapsed.
            </div>
            <p className="text-white/70 text-sm max-w-2xl mx-auto leading-relaxed">
              The technician has taken the car off the ramp — he can&apos;t sit idle. He&apos;s started the next job.
              If the work IS sold, workshop control now has to re-juggle the diary to bring it back in.
              Sometimes the customer goes home with the work undone.
            </p>
          </div>
        </div>
      </section>

      {/* -------- NEW WAY -------- */}
      <section className="bg-background">
        <div className="max-w-6xl mx-auto px-6 py-20">
          <div className="text-[10px] font-bold uppercase tracking-[0.25em] text-primary">
            With {brand.productName}{trade}
          </div>
          <h2 className="font-display font-bold text-3xl md:text-4xl mt-3 mb-3">
            Same morning. Same car. Nine steps. Done.
          </h2>
          <p className="text-muted-foreground max-w-3xl leading-relaxed mb-12">
            Each step happens automatically the moment the previous one completes. No printouts.
            No diary jugglers. No phone-tag with the customer.
          </p>

          <ol className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="tp-new-flow">
            {NEW_FLOW.map((step, idx) => {
              const Icon = step.icon;
              return (
                <li
                  key={idx}
                  className="group rounded-xl border border-primary/20 bg-card hover:bg-primary/[0.06] hover:border-primary/40 p-5 transition-colors duration-200"
                  data-testid={`tp-step-${idx + 1}`}
                >
                  <div className="flex items-center justify-between mb-3">
                    <div className="w-9 h-9 rounded-md bg-primary/15 text-primary grid place-items-center">
                      <Icon className="w-5 h-5" strokeWidth={2.2} />
                    </div>
                    <div className="font-mono-plex text-2xl font-bold text-primary/40 group-hover:text-primary/70 transition-colors">
                      {String(idx + 1).padStart(2, "0")}
                    </div>
                  </div>
                  <h3 className="font-bold text-[14.5px] leading-snug mb-2">{step.title}</h3>
                  <p className="text-[12.5px] text-muted-foreground leading-relaxed mb-3">{step.body}</p>
                  {step.duration && step.duration !== "—" && (
                    <div className="inline-flex items-center gap-1.5 text-[10.5px] font-bold uppercase tracking-wider text-primary">
                      <Clock className="w-3 h-3" />
                      {step.duration}
                    </div>
                  )}
                </li>
              );
            })}
          </ol>

          <div className="mt-12 rounded-xl border border-primary/40 bg-primary/[0.06] p-8 text-center">
            <div className="text-primary font-mono-plex text-xs font-bold uppercase tracking-[0.3em]">
              And the customer?
            </div>
            <div className="font-display font-bold text-3xl md:text-4xl mt-3 mb-2">
              4 minutes to tap Approve.
            </div>
            <p className="text-muted-foreground text-sm max-w-2xl mx-auto leading-relaxed">
              No phone tag. No printed estimate. No jargon. They see their car, in plain English,
              on the phone that&apos;s already in their hand — and they tap Approve.
            </p>
          </div>
        </div>
      </section>

      {/* -------- CTA -------- */}
      <section className="border-t border-white/10 bg-black">
        <div className="max-w-4xl mx-auto px-6 py-20 text-center">
          <h2 className="font-display font-bold text-3xl md:text-4xl text-white mb-4">
            Ready to see it in your dealership?
          </h2>
          <p className="text-white/65 text-base leading-relaxed mb-8 max-w-2xl mx-auto">
            £1,000 onboarding, then your choice of £1,500/month flat SaaS or 3% commission on
            approved extras. 12-month minimum term. 30-day money-back guarantee on Option A.
          </p>
          <div className="flex flex-wrap items-center justify-center gap-3">
            <Link to="/pilot" className="btn-primary inline-flex items-center gap-2" data-testid="tp-cta-pilot">
              See the 30-day pilot <ArrowRight className="w-4 h-4" />
            </Link>
            <Link to="/" className="btn-outline inline-flex items-center gap-2" data-testid="tp-cta-home">
              Back to {brand.productName}{trade}
            </Link>
          </div>
          <p className="text-[10.5px] text-white/35 italic mt-6">
            Registered UK Trade Mark No. UK00004398263 &middot; Company No. 17211707 &middot; ICO ZC144538
          </p>
        </div>
      </section>
    </div>
  );
}
